import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InboxContext = createContext();

export const useInbox = () => {
  const context = useContext(InboxContext);
  if (!context) {
    throw new Error('useInbox must be used within InboxProvider');
  }
  return context;
};

export const InboxProvider = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch conversations (inbox list)
  const fetchConversations = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('skillsos_token');
      if (!token) {
        console.log('No token available, skipping fetch');
        return;
      }
      
      const response = await axios.get(`${API}/messages/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConversations(response.data.conversations || []);
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('Not authenticated - cannot fetch conversations');
      } else {
        console.error('Failed to fetch conversations:', error);
      }
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    
    try {
      const token = localStorage.getItem('skillsos_token');
      if (!token) {
        setUnreadCount(0);
        return;
      }
      
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const count = response.data.unread_count || 0;
      console.log('📬 Fetched unread count:', count);
      setUnreadCount(count);
    } catch (error) {
      if (error.response?.status === 401) {
        setUnreadCount(0);
      } else {
        console.error('Failed to fetch unread count:', error);
      }
    }
  };

  // Fetch messages for a specific conversation
  const fetchConversationMessages = async (conversationId) => {
    if (!user || !conversationId) return;
    
    // Handle virtual/new conversations - they have no messages yet
    if (conversationId.startsWith('new_')) {
      console.log('📬 [InboxContext] Virtual conversation detected, no messages to fetch');
      setConversationMessages([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('skillsos_token');
      const response = await axios.get(`${API}/messages/inbox`, {
        params: { conversation_id: conversationId },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConversationMessages(response.data.messages || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch conversation messages:', error);
      setConversationMessages([]);
      setLoading(false);
    }
  };

  // Send a message
  const sendMessage = async (recipientId, message, messageType = 'direct', subject = null, bookingId = null) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const token = localStorage.getItem('skillsos_token');
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }
    
    console.log('🔑 Sending message with auth:', {
      hasToken: !!token,
      tokenLength: token?.length,
      recipientId,
      messageLength: message?.length,
      bookingId
    });
    
    try {
      const response = await axios.post(
        `${API}/messages/send`,
        {
          recipient_id: recipientId,
          message: message,
          message_type: messageType,
          subject: subject,
          booking_id: bookingId  // Add booking_id to link messages to bookings
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Message sent successfully:', response.data);
      
      // Show warning toast if contact info was filtered
      if (response.data.contact_filtered || response.data.warning) {
        toast.warning(
          response.data.warning || 'За Ваша сигурност, данните за контакт са скрити. Те ще бъдат видими след потвърдена резервация.',
          { duration: 6000 }
        );
      }
      
      // Refresh conversations and unread count
      await fetchConversations();
      await fetchUnreadCount();
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to send message:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        hasToken: !!token
      });
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      }
      
      throw error;
    }
  };

  // Mark message as read
  const markMessageAsRead = async (messageId) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('skillsos_token');
      await axios.patch(
        `${API}/messages/${messageId}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      await fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  // Mark all messages in a conversation as read
  const markConversationAsRead = async (conversationId) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('skillsos_token');
      await axios.patch(
        `${API}/messages/conversation/${conversationId}/read-all`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      await fetchUnreadCount();
      await fetchConversations();
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  };

  // Fetch user contacts
  const fetchContacts = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('skillsos_token');
      if (!token) return;
      
      const response = await axios.get(`${API}/messages/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContacts(response.data.contacts || []);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Failed to fetch contacts:', error);
      }
    }
  };

  // Initial load
  useEffect(() => {
    if (user && localStorage.getItem('skillsos_token')) {
      fetchConversations();
      fetchUnreadCount();
      fetchContacts();
      
      // Poll for new messages every 10 seconds for near real-time notifications
      const interval = setInterval(() => {
        if (localStorage.getItem('skillsos_token')) {
          fetchConversations();
          fetchUnreadCount();
        }
      }, 10000);
      
      return () => clearInterval(interval);
    } else {
      // Clear data when user logs out
      setConversations([]);
      setUnreadCount(0);
      setContacts([]);
    }
  }, [user]);

  const value = {
    conversations,
    unreadCount,
    selectedConversation,
    setSelectedConversation,
    conversationMessages,
    contacts,
    loading,
    fetchConversations,
    fetchUnreadCount,
    fetchConversationMessages,
    sendMessage,
    markMessageAsRead,
    markConversationAsRead,
    fetchContacts
  };

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
};
